import { useState } from 'react';
import { useThreatModel, regenerateThreatModel, type ThreatPrediction } from '../../hooks/useSecOps';

function RiskGauge({ score }: { score: number }) {
  const color = score >= 75 ? '#dc2626' : score >= 50 ? '#d97706' : score >= 25 ? '#2563eb' : '#16a34a';
  const label = score >= 75 ? 'Critical' : score >= 50 ? 'High' : score >= 25 ? 'Moderate' : 'Low';
  const rotation = (score / 100) * 180 - 90;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-16 overflow-hidden">
        <svg viewBox="0 0 120 60" className="w-full h-full">
          <path d="M10 55 A50 50 0 0 1 110 55" fill="none" stroke="#e5e7eb" strokeWidth="8" strokeLinecap="round" />
          <path d="M10 55 A50 50 0 0 1 110 55" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${(score / 100) * 157} 157`} />
          <line x1="60" y1="55" x2="60" y2="20" stroke={color} strokeWidth="2" strokeLinecap="round"
            transform={`rotate(${rotation} 60 55)`} />
        </svg>
      </div>
      <span className="text-2xl font-bold mt-1" style={{ color }}>{score}</span>
      <span className="text-xs text-gray-500">{label} Risk</span>
    </div>
  );
}

function LikelihoodBar({ score }: { score: number }) {
  const color = score >= 75 ? '#dc2626' : score >= 50 ? '#ea580c' : score >= 25 ? '#d97706' : '#2563eb';
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className="h-2 rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-semibold w-8 text-right" style={{ color }}>{score}%</span>
    </div>
  );
}

export default function ThreatModelingPage() {
  const { data: response, loading } = useThreatModel();
  const model = response?.data;
  const [regenerating, setRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await regenerateThreatModel();
      window.location.reload();
    } finally {
      setRegenerating(false);
    }
  };

  if (loading && !model) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Predictive Threat Model</h1>
        <div className="animate-pulse space-y-4">
          <div className="bg-gray-200 h-48 rounded-lg" />
          <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="bg-gray-200 h-32 rounded-lg" />)}</div>
        </div>
      </div>
    );
  }

  if (!model) return null;

  const { predictions, risk_profile, generated_at } = model;

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Predictive Threat Model</h1>
          <p className="text-xs text-gray-400 mt-1">AI-powered risk analysis based on your 90-day security profile</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-gray-400">Generated {new Date(generated_at).toLocaleDateString()}</span>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {regenerating ? 'Analyzing...' : 'Regenerate'}
          </button>
        </div>
      </div>

      {/* ── Risk Profile Summary ── */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-900 rounded-xl p-6 text-white">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
          <div className="flex justify-center">
            <RiskGauge score={risk_profile.overall_score} />
          </div>
          <div>
            <p className="text-xs text-indigo-300 uppercase tracking-wide">Top Risk Category</p>
            <p className="text-lg font-semibold mt-1">{risk_profile.top_risk_category.replace(/_/g, ' ')}</p>
            {risk_profile.industry && (
              <p className="text-xs text-indigo-300 mt-2">Industry: {risk_profile.industry}</p>
            )}
            {risk_profile.compliance_score != null && (
              <p className="text-xs text-indigo-300">Compliance: {risk_profile.compliance_score}/100</p>
            )}
          </div>
          <div>
            <p className="text-xs text-red-300 uppercase tracking-wide mb-2">Coverage Gaps</p>
            {risk_profile.coverage_gaps.length === 0 ? (
              <p className="text-xs text-indigo-300 italic">None identified</p>
            ) : (
              <div className="space-y-1">
                {risk_profile.coverage_gaps.slice(0, 4).map((gap, i) => (
                  <span key={i} className="inline-block text-[10px] px-2 py-0.5 bg-red-500/20 text-red-200 rounded-full mr-1 mb-1">{gap}</span>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-xs text-green-300 uppercase tracking-wide mb-2">Strengths</p>
            {risk_profile.strengths.length === 0 ? (
              <p className="text-xs text-indigo-300 italic">Building baseline...</p>
            ) : (
              <div className="space-y-1">
                {risk_profile.strengths.slice(0, 4).map((s: string, i: number) => (
                  <span key={i} className="inline-block text-[10px] px-2 py-0.5 bg-green-500/20 text-green-200 rounded-full mr-1 mb-1">{s}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Threat Predictions ── */}
      <h2 className="text-sm font-semibold text-gray-700">Predicted Attack Vectors (Next 90 Days)</h2>
      {predictions.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-sm text-gray-400">Not enough data for predictions yet. Build up 90 days of alert history.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {predictions.map((pred: ThreatPrediction, i: number) => (
            <div key={i} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-300">#{i + 1}</span>
                    <h3 className="text-sm font-semibold text-gray-900">{pred.attack_vector}</h3>
                    {pred.risk_multiplier && (
                      <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium">{pred.risk_multiplier}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{pred.description}</p>
                </div>
              </div>
              <div className="mb-3">
                <p className="text-[10px] text-gray-400 uppercase mb-1">Likelihood</p>
                <LikelihoodBar score={pred.likelihood_score} />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-[10px] text-gray-400 uppercase mb-1">Recommendation</p>
                  <p className="text-xs text-gray-700">{pred.recommendation}</p>
                </div>
                {pred.mitre_techniques.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-w-[200px] justify-end">
                    {pred.mitre_techniques.map((t: string) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded font-mono">{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
